/** Gender options for measurement algorithms */
export type Gender = 'male' | 'female' | 'other';

/** Unit system for displaying measurements */
export type UnitSystem = 'cm' | 'inches';

/** Convert cm to inches (rounded to 1 decimal) */
export function convertCmToInches(cm: number): number {
  return Math.round((cm / 2.54) * 10) / 10;
}

/** Format a measurement value with the given unit system */
export function formatMeasurement(valueCm: number, unit: UnitSystem): string {
  if (unit === 'inches') {
    return `${convertCmToInches(valueCm)} in`;
  }
  return `${valueCm} cm`;
}

/** What the user is wearing during photo capture */
export type WornClothingType =
  | 'tight_fitness'
  | 'underwear'
  | 'tshirt_jeans'
  | 'dress_shirt'
  | 'light_traditional'
  | 'heavy_traditional';

/** Clothing compensation values in cm (subtracted from circumferences) */
export const CLOTHING_COMPENSATION: Record<WornClothingType, ClothingCompensation> = {
  tight_fitness: { chest: 0, waist: 0, hip: 0, thigh: 0 },
  underwear: { chest: 0, waist: 0, hip: 0, thigh: 0 },
  tshirt_jeans: { chest: 1.5, waist: 1.5, hip: 1.5, thigh: 1.5 },
  dress_shirt: { chest: 1.2, waist: 1.2, hip: 0, thigh: 0 },
  light_traditional: { chest: 2.5, waist: 2.5, hip: 2.5, thigh: 0 },
  heavy_traditional: { chest: 4.0, waist: 4.0, hip: 4.0, thigh: 4.0 },
};

export interface ClothingCompensation {
  chest: number;
  waist: number;
  hip: number;
  thigh: number;
}

/** How the user calibrated the scale */
export type ReferenceObjectType =
  | 'manual_height'
  | 'a4_paper'
  | 'credit_card'
  | 'ruler';

/** Calibration result */
export interface CalibrationResult {
  pixelsPerCm: number;
  method: ReferenceObjectType;
  confidence: number;
}

/** A single body landmark from MediaPipe Pose */
export interface Landmark {
  x: number; // normalized [0, 1]
  y: number; // normalized [0, 1]
  z: number; // depth estimate
  visibility: number; // confidence [0, 1]
}

/** 33 MediaPipe Pose landmarks */
export type PoseLandmarks = Landmark[];

/** Detection result from a single photo */
export interface DetectionResult {
  landmarks: PoseLandmarks;
  segmentationMask: ImageData | null;
  imageWidth: number;
  imageHeight: number;
}

/** Confidence level for a measurement */
export type ConfidenceLevel = 'high' | 'medium' | 'low';

/** Method used to derive a measurement */
export type MeasurementMethod =
  | 'direct_landmark'
  | 'silhouette_enhanced'
  | 'computed_circumference'
  | 'multi_view_circumference'
  | 'proportional_estimate';

/** A single measurement with metadata */
export interface MeasurementValue {
  valueCm: number;
  confidence: number; // 0-1
  confidenceLevel: ConfidenceLevel;
  method: MeasurementMethod;
}

/** All 30 body measurements */
export interface BodyMeasurements {
  height: MeasurementValue;
  neckCircumference: MeasurementValue;
  shoulderWidth: MeasurementValue;
  chestCircumference: MeasurementValue;
  waistCircumference: MeasurementValue;
  hipCircumference: MeasurementValue;
  armLength: MeasurementValue;
  upperArmCircumference: MeasurementValue;
  wristCircumference: MeasurementValue;
  inseamLength: MeasurementValue;
  outseamLength: MeasurementValue;
  thighCircumference: MeasurementValue;
  backLength: MeasurementValue;
  frontLength: MeasurementValue;
  torsoLength: MeasurementValue;
  acrossBackWidth: MeasurementValue;
  acrossChestWidth: MeasurementValue;
  kneeCircumference: MeasurementValue;
  calfCircumference: MeasurementValue;
  ankleCircumference: MeasurementValue;
  trouserLength: MeasurementValue;
  rise: MeasurementValue;
  sleeveLength34: MeasurementValue;
  agbadaLength: MeasurementValue;
  headCircumference: MeasurementValue;
  // Women-specific
  bustPoint: MeasurementValue;
  underBustDrop: MeasurementValue;
  underBustCircumference: MeasurementValue;
  // Skirt/dress
  kneeLength: MeasurementValue;
  dressLength: MeasurementValue;
}

/** Keys of BodyMeasurements for iteration */
export type MeasurementKey = keyof BodyMeasurements;

/** Human-readable labels for measurements */
export const MEASUREMENT_LABELS: Record<MeasurementKey, string> = {
  height: 'Height',
  neckCircumference: 'Neck Circumference',
  shoulderWidth: 'Shoulder Width',
  chestCircumference: 'Chest/Bust Circumference',
  waistCircumference: 'Waist Circumference',
  hipCircumference: 'Hip Circumference',
  armLength: 'Arm Length',
  upperArmCircumference: 'Upper Arm Circumference',
  wristCircumference: 'Wrist Circumference',
  inseamLength: 'Inseam Length',
  outseamLength: 'Outseam Length',
  thighCircumference: 'Thigh Circumference',
  backLength: 'Back Length',
  frontLength: 'Front Length',
  torsoLength: 'Torso Length',
  acrossBackWidth: 'Across Back Width',
  acrossChestWidth: 'Across Chest Width',
  kneeCircumference: 'Knee Circumference',
  calfCircumference: 'Calf Circumference',
  ankleCircumference: 'Ankle Circumference',
  trouserLength: 'Trouser Length',
  rise: 'Rise (Waist to Crotch)',
  sleeveLength34: 'Sleeve Length (3/4)',
  agbadaLength: 'Agbada Length',
  headCircumference: 'Head Circumference',
  bustPoint: 'Bust Point',
  underBustDrop: 'Under Bust Drop',
  underBustCircumference: 'Under Bust Circumference',
  kneeLength: 'Knee Length',
  dressLength: 'Dress Length',
};

// ─── Tailor-Ready Garment Categories ─────────────────────────

/** Tailor measurement category for display */
export type TailorGarmentCategory = 'shirt' | 'trouser' | 'blouse' | 'skirt' | 'dress';

/** A measurement with its Nigerian tailor term */
export interface TailorMeasurement {
  key: MeasurementKey;
  tailorLabel: string;
}

/** Men/Boys Shirt measurements */
export const MALE_SHIRT_MEASUREMENTS: TailorMeasurement[] = [
  { key: 'shoulderWidth', tailorLabel: 'Shoulder' },
  { key: 'neckCircumference', tailorLabel: 'Neck' },
  { key: 'chestCircumference', tailorLabel: 'Chest' },
  { key: 'waistCircumference', tailorLabel: 'Tummy' },
  { key: 'hipCircumference', tailorLabel: 'Hip' },
  { key: 'armLength', tailorLabel: 'Sleeve Length' },
  { key: 'upperArmCircumference', tailorLabel: 'Sleeve Round' },
  { key: 'wristCircumference', tailorLabel: 'Round Arm' },
  { key: 'backLength', tailorLabel: 'Length' },
];

/** Men/Boys Trouser measurements */
export const MALE_TROUSER_MEASUREMENTS: TailorMeasurement[] = [
  { key: 'waistCircumference', tailorLabel: 'Waist' },
  { key: 'thighCircumference', tailorLabel: 'Lap' },
  { key: 'kneeCircumference', tailorLabel: 'Knee' },
  { key: 'ankleCircumference', tailorLabel: 'Boot' },
  { key: 'trouserLength', tailorLabel: 'Length' },
];

/** Women Blouse measurements */
export const FEMALE_BLOUSE_MEASUREMENTS: TailorMeasurement[] = [
  { key: 'shoulderWidth', tailorLabel: 'Shoulder' },
  { key: 'bustPoint', tailorLabel: 'Bust Point' },
  { key: 'underBustDrop', tailorLabel: 'Under Bust' },
  { key: 'frontLength', tailorLabel: 'Half Length' },
  { key: 'chestCircumference', tailorLabel: 'Bust' },
  { key: 'underBustCircumference', tailorLabel: 'Under Bust Round' },
  { key: 'waistCircumference', tailorLabel: 'Waist' },
  { key: 'sleeveLength34', tailorLabel: 'Short Sleeve Length' },
  { key: 'upperArmCircumference', tailorLabel: 'Short Sleeve Round' },
  { key: 'hipCircumference', tailorLabel: 'Hip' },
  { key: 'backLength', tailorLabel: 'Blouse Length' },
];

/** Women Skirt measurements */
export const FEMALE_SKIRT_MEASUREMENTS: TailorMeasurement[] = [
  { key: 'waistCircumference', tailorLabel: 'Waist' },
  { key: 'hipCircumference', tailorLabel: 'Hip' },
  { key: 'kneeLength', tailorLabel: 'Knee Length' },
  { key: 'trouserLength', tailorLabel: 'Full Length' },
];

/** Girl Dress measurements */
export const GIRL_DRESS_MEASUREMENTS: TailorMeasurement[] = [
  { key: 'shoulderWidth', tailorLabel: 'Shoulder' },
  { key: 'frontLength', tailorLabel: 'Half Length' },
  { key: 'chestCircumference', tailorLabel: 'Bust Round' },
  { key: 'waistCircumference', tailorLabel: 'Waist' },
  { key: 'armLength', tailorLabel: 'Sleeve Length' },
  { key: 'upperArmCircumference', tailorLabel: 'Sleeve Round' },
  { key: 'dressLength', tailorLabel: 'Dress Length' },
];

/** Garment fit types */
export type FitType = 'very_loose' | 'loose' | 'regular' | 'fitted';

/** Garment category */
export type GarmentCategory =
  | 'nigerian_traditional'
  | 'west_african'
  | 'east_african'
  | 'general_african'
  | 'fusion';

/** A garment profile definition */
export interface GarmentProfile {
  id: string;
  name: string;
  category: GarmentCategory;
  fitType: FitType;
  requiredMeasurements: MeasurementKey[];
  easeAllowances: Partial<Record<MeasurementKey, number>>;
  tailorNotes: string[];
}

/** Cut measurements for a specific garment */
export interface GarmentMeasurements {
  garmentType: string;
  garmentName: string;
  fitType: FitType;
  measurements: Record<string, { body: number; ease: number; cut: number }>;
  tailorNotes: string[];
}

/** Application step in the measurement flow */
export type AppStep =
  | 'welcome'
  | 'clothing'
  | 'reference'
  | 'capture_front'
  | 'capture_side'
  | 'capture_rotation'
  | 'capture_session'
  | 'photo_review'
  | 'processing'
  | 'side_prompt'
  | 'processing_side'
  | 'processing_rotation'
  | 'results'
  | 'garment_select'
  | 'garment_results';

/** Capture mode for accuracy selection */
export type CaptureMode = 'quick' | 'side' | 'rotation';

/** A single keyframe captured during rotation */
export interface RotationKeyframe {
  /** JPEG data URL */
  photoDataUrl: string;
  /** Estimated rotation angle in degrees (0-360) */
  angle: number;
  /** Silhouette widths at key Y-levels in pixels */
  silhouetteWidths: {
    chestPx: number;
    waistPx: number;
    hipPx: number;
  };
  /** Pose landmarks if detected (may be null for back-facing angles) */
  landmarks: PoseLandmarks | null;
  /** Timestamp when captured */
  timestamp: number;
}

/** Processing progress */
export interface ProcessingProgress {
  step: string;
  message: string;
  percent: number;
}
